import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import { IconButton } from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import TextField from "../carbon/Form/TextField";
import { MainClientsFormFields } from "./mainClientsValidationSchema";

export interface MainClientRowProps {
  index: number;
  control: Control<MainClientsFormFields>;
  onRemove: () => void;
}

/**
 * One row of `MainClientsForm`'s `useFieldArray` — same "compose `carbon/Form/*` primitives per
 * row, no new shared row primitive" pattern `AddressRow.tsx`/`BankingRow.tsx`/`ContactRow.tsx`
 * established (Tasks 8-10). All four fields are plain `TextField`s — no field on this row needs a
 * `SelectField`/checkbox/date/file control.
 *
 * Real, disclosed behaviour change from the pre-Carbon form, same directional change Tasks 8-10
 * already made for their own repeatable lists: every main client — already-persisted or freshly
 * added — is now editable in place (the pre-Carbon form only ever let you ADD new rows or remove
 * an unsaved one). Safe for the identical reason: `CompanyService#updateCompanyClients` (backend)
 * already fully REPLACES the main-clients list on every save.
 */
const MainClientRow = ({ index, control, onRemove }: MainClientRowProps) => {
  const { t } = useTranslation();

  return (
    <div className="main-clients-form__row">
      <div className="main-clients-form__row-grid">
        <TextField
          name={`mainClients.${index}.clientName`}
          label={t("companyProfile.clientName")}
          control={control}
        />
        <TextField
          name={`mainClients.${index}.contactPersonName`}
          label={t("companyProfile.contactPersonName")}
          control={control}
        />
        <TextField
          name={`mainClients.${index}.contactEmail`}
          label={t("companyProfile.contactEmail")}
          control={control}
          type="email"
        />
        <TextField
          name={`mainClients.${index}.contactPhone`}
          label={t("companyProfile.contactPhone")}
          control={control}
          type="tel"
        />
      </div>

      <div className="main-clients-form__row-actions">
        <IconButton
          kind="ghost"
          label={t("cta.remove")}
          align="top"
          onClick={onRemove}
        >
          <TrashCan />
        </IconButton>
      </div>
    </div>
  );
};

export default MainClientRow;
